import React, {useRef, useCallback} from 'react';

import {observer} from 'mobx-react';
import {FormControlLabel, Radio, RadioGroup} from '@mui/material';

import {useStores} from '../../../Hooks';
import {runProcedureInEnum} from '../../../serverClientShared/sharedEnums';
import ConditionalComponent from '../../Shared/ConditionalComponent';
import {FrontlineCheckbox} from '../../Shared/FrontlineCheckbox';

const ConnectedModelsSection = observer(({
    threeDModels,
    connectedModels,
    onToggleModel,
    allowRun,
    onSetAllowRun,
    disabled,
}) => {
    const {modalStore, generalStore} = useStores();
    const anchorRef = useRef(null);

    const openModelSelector = useCallback(() => {
        if (disabled) return;
        const currentIds = connectedModels || [];
        modalStore.modalData.genericSelectData = {
            anchorEl: anchorRef.current,
            title: generalStore.translate('connectToA') + ' 3D ' + generalStore.translate('model'),
            searchPlaceHolder: generalStore.translate('search') + '...',
            isMultipleChoices: true,
            showActionBar: false,
            showSelectedCount: true,
            fetchData: async () => ({
                data: threeDModels.map((m) => ({...m, value: m.ProcedureIdentifier})),
                totalCount: threeDModels.length,
                initialSelected: currentIds,
            }),
            renderItem: (item, isChecked, onToggle) => (
                <ModelRow key={item.ProcedureIdentifier} model={item} checked={isChecked} onToggle={onToggle} />
            ),
            handleSelect: (selectedIds) => {
                const current = new Set(connectedModels || []);
                const next = new Set(selectedIds);
                for (const id of next) {
                    if (!current.has(id)) onToggleModel(id);
                }
                for (const id of current) {
                    if (!next.has(id)) onToggleModel(id);
                }
            },
        };
        modalStore.isGenericSelectSearchModalOpen = true;
    }, [disabled, connectedModels, threeDModels, modalStore, generalStore, onToggleModel]);

    const hasModels = connectedModels?.length > 0;
    const noModelsAvailable = !threeDModels?.length;

    const labelStyle = {fontSize: 11, fontWeight: 600, color: '#36415D', letterSpacing: 0.5, marginBottom: 4};
    const radioLabelSx = {'.MuiFormControlLabel-label': {fontSize: 13, color: '#36415D'}};

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 8, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto'}}>
            <span style={labelStyle}>
                {generalStore.translate('connectToA') + ' 3D ' + generalStore.translate('model')}
            </span>
            <ConditionalComponent if={noModelsAvailable}>
                <span style={{fontSize: 13, color: '#868d9e'}}>{generalStore.translate('noModels')}</span>
            </ConditionalComponent>
            <ConditionalComponent if={!noModelsAvailable}>
                <div
                    ref={anchorRef}
                    onClick={openModelSelector}
                    style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, border: '1px solid #d9e0f0', backgroundColor: '#fff', cursor: 'pointer', minHeight: 34}}
                >
                    {!hasModels ? (
                        <span style={{fontSize: 13, color: '#868d9e'}}>{generalStore.translate('selectModel')}</span>
                    ) : (
                        connectedModels.map((id) => {
                            const model = threeDModels.find((m) => m.ProcedureIdentifier === id);
                            if (!model) return null;
                            return (
                                <span key={id} style={{fontSize: 12, padding: '3px 12px', borderRadius: 14, backgroundColor: '#f0f2f7', color: '#36415D', whiteSpace: 'nowrap'}}>
                                    {model.ProcedureName}
                                </span>
                            );
                        })
                    )}
                </div>
            </ConditionalComponent>
            <ConditionalComponent if={!noModelsAvailable}>
                <div style={{marginTop: 2}}>
                    <span style={labelStyle}>{generalStore.translate('runInteractiveFlowIn')}</span>
                    <RadioGroup
                        value={!hasModels ? runProcedureInEnum.TWO_D : allowRun || runProcedureInEnum.TWO_D}
                        onChange={(e) => onSetAllowRun(e.target.value)}
                        row
                        style={{marginTop: 0, gap: 4}}
                    >
                        <FormControlLabel value={runProcedureInEnum.TWO_D} control={<Radio size="small" sx={{padding: '4px'}} />} label={generalStore.translate('only') + ' 2D'} sx={radioLabelSx} />
                        <FormControlLabel value={runProcedureInEnum.THREE_D} control={<Radio size="small" sx={{padding: '4px'}} />} label={generalStore.translate('only') + ' 3D'} disabled={!hasModels} sx={radioLabelSx} />
                        <FormControlLabel value={runProcedureInEnum.BOTH} control={<Radio size="small" sx={{padding: '4px'}} />} label={generalStore.translate('both')} disabled={!hasModels} sx={radioLabelSx} />
                    </RadioGroup>
                </div>
            </ConditionalComponent>
        </div>
    );
});

const ModelRow = ({model, checked, onToggle}) => (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-windowsandmenus-hover" onClick={onToggle}>
        <FrontlineCheckbox isChecked={checked} onChange={() => {}} size={20} isWithPadding={false} />
        <span className="text-sm text-main-normal truncate">{model.ProcedureName}</span>
    </div>
);

export default ConnectedModelsSection;
